import { useSelfHiringContext } from "@/app/contexts/SelfHiringContext"
import { cn } from "@/app/utils/cn"
import { addMoneySimbols } from "@/app/utils/formatMoneyValue"
import { OptionType } from "@/app/utils/optionSelectType"
import { getPartnerBgColor, getPartnerImage } from "@/app/utils/partners"
import { PartnerType } from "@/app/utils/partners.enum"
import { Button } from "@/view/components/ui/button"
import { addMonths, format, isValid, parseISO } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import Select from 'react-select'
import { customStyles } from "./styles"
import { CreditCard } from "@/view/components/CreditCard"
import { useFormContext } from "react-hook-form"
import { useInstallments } from "./useInstallments"
import { CondicaoCredito } from "@/app/services/panService/generateOffers"
import { CreditPartner } from "../../../useCreditController"
import { Skeleton } from "@/view/components/ui/skeleton"

interface InstallmentsProps {
  onNext: () => void
}
export const Installments = ({ onNext }: InstallmentsProps) => {
  const methods = useFormContext<any>()
  const { handleChangeInstallment, reloadTable, handleMakeProposal } = useInstallments(methods)

  const [selectedOption, setSelectedOption] = useState<OptionType | null>(null)
  const {
    type,
    fgtsData,
    panData,
    cltData,
  } = useSelfHiringContext()
  const [openItem, setOpenItem] = useState<{ [index: number]: boolean }>({})
  const isCltInitialLoading = type === PartnerType.CLT && !cltData
  if (!fgtsData?.table && !panData && !cltData && !isCltInitialLoading) return null

  const panOffer = useMemo(() => panData?.state?.offers?.conditions?.find((cond) => cond.sucesso) ?? null, [panData?.state?.offers?.conditions])

  const getSeguro = (offer: CondicaoCredito | null) => {
    if (!offer) return null
    return offer.despesas?.find((d) => d.grupo === "SEGURO")
  }
  const tableFGTS = useMemo(() => fgtsData?.table ?? null, [fgtsData?.table])

  const tableCLT = useMemo(() => {
    if (!cltData?.tables?.length) return null
    return (
      cltData.tables.find((t) => t.Prazo === cltData.selectedPrazo) ??
      cltData.tables[0]
    )
  }, [cltData?.tables, cltData?.selectedPrazo])

  const cltInstallments = useMemo(() => {
    if (!tableCLT?.Prazo || !tableCLT?.ValorParcela) return []
    const baseDateRaw = tableCLT.DataPrimeiroVencimento
    const baseDate = baseDateRaw ? parseISO(baseDateRaw) : null
    return Array.from({ length: tableCLT.Prazo }, (_, i) => ({
      number: i + 1,
      value: tableCLT.ValorParcela,
      dueDate:
        baseDate && isValid(baseDate)
          ? addMonths(baseDate, i).toISOString()
          : '',
    }))
  }, [tableCLT])

  const mapping = {
    [PartnerType.FGTS]: {
      availableValue: tableFGTS?.ValorLiquidoCliente,
      blockedValue: tableFGTS?.ValorBloqueado,
      blockedLabel: 'Valor Bloqueado',
      installments: tableFGTS?.Parcelas?.map((parcela) => ({
        number: parcela.NumeroParcela,
        value: parcela.ValorParcela,
        dueDate: parcela.DataVencimento,
      })),
      tc: tableFGTS?.ValorTC ?? 0,
      iof: tableFGTS?.IOF ?? 0,
      seguro: tableFGTS?.ValorSeguro ?? 0,
      prazo: tableFGTS?.Prazo ?? 0,
    },
    [PartnerType.PAN]: {
      availableValue: panOffer?.valor_liquido,
      blockedValue: 0,
      blockedLabel: 'Valor Bloqueado',
      installments: panOffer?.parcelas?.map((parcela, i) => ({
        number: i + 1,
        value: panData?.state?.selectedValue ?? 0,
        dueDate: (parcela as string) || '',
      })),
      tc: 0,
      iof: panOffer?.valor_iof ?? 0,
      seguro: getSeguro(panOffer)?.valor_calculado ?? 0,
      prazo: panOffer?.prazo ?? 0,
    },
    [PartnerType.CLT]: {
      availableValue: tableCLT?.ValorLiquidoCliente ?? 0,
      blockedValue: tableCLT?.ValorBruto ?? 0,
      blockedLabel: 'Valor Total',
      installments: cltInstallments,
      tc: tableCLT?.ValorTC ?? 0,
      iof: tableCLT?.ValorIOC ?? 0,
      seguro: tableCLT?.ValorSeguro ?? 0,
      prazo: tableCLT?.Prazo ?? 0,
    },
  }

  const table = useMemo(() => mapping[type as keyof typeof mapping] ?? null, [type, mapping])

  const items = [
    {
      label: 'Valor Disponível',
      value: addMoneySimbols(table.availableValue ?? 0),
    },
    {
      label: table.blockedLabel ?? 'Valor Bloqueado',
      value: addMoneySimbols(table.blockedValue ?? 0),
      subItem: SubValues,
    },
    {
      label: 'Parcelas',
      subItem: InstallmentItens,
    }
  ]

  const options = useMemo(() => {
    if (type === PartnerType.PAN) {
      const prazoOptions = panData?.state?.prazoOptions ?? []
      return prazoOptions.map((p) => ({
        label: p.label,
        value: `${p.value}`,
      })) as OptionType[]
    }

    if (type === PartnerType.CLT) {
      const prazos = cltData?.prazos ?? []
      return prazos.map((p) => ({
        label: `${p}X`,
        value: `${p}`,
      })) as OptionType[]
    }

    return Array.from({ length: table.prazo ?? 0 }, (_, i) => ({
      label: `${i + 1}X`,
      value: `${i + 1}`,
    })) as OptionType[]
  }, [panData?.state?.prazoOptions, cltData?.prazos, table.prazo, type])

  const handleSelectInstallment = (opt: OptionType) => {
    setSelectedOption(opt)

    if (opt.value === 'maximo') {
      if (type === PartnerType.PAN || type === PartnerType.CLT) {
        const maxPrazo = Math.max(...(options.map((o) => Number(o.value)).filter((n) => Number.isFinite(n)) as number[]))
        if (Number.isFinite(maxPrazo)) void handleChangeInstallment(maxPrazo)
      } else {
        void handleChangeInstallment(undefined)
      }
      return
    }

    const value = Number(opt.value)
    if (!Number.isFinite(value)) return

    void handleChangeInstallment(value)
  }

  useEffect(() => {
    const prazoAtual =
      type === PartnerType.PAN
        ? panData?.state?.selectedPrazo
        : type === PartnerType.CLT
          ? cltData?.selectedPrazo
          : table.prazo
    if (!prazoAtual) {
      setSelectedOption(null)
      return
    }

    setSelectedOption(options.find(option => option.value === `${prazoAtual}`) ?? null)
  }, [options, panData?.state?.selectedPrazo, cltData?.selectedPrazo, table.prazo, type])

  const isReloading = reloadTable || isCltInitialLoading

  const handleNext = () => {
    if (type === PartnerType.PAN) {
      handleMakeProposal()
    }
    onNext()
  }

  return (
    <>
      <div
        className="flex h-14 w-full shrink-0 items-center justify-center rounded-xl mb-4"
        style={{ backgroundColor: getPartnerBgColor(type as PartnerType) }}
      >
        <img
          src={getPartnerImage(type as CreditPartner)}
          alt={(type as string) ?? ""}
          className={cn("h-14 object-contain", type === PartnerType.PAN && "h-18 max-w-32")}
        />
      </div>
      <CreditCard classNameBackButton="md:top-4" className="gap-4 pt-12 sm:pt-8 md:pt-12">

        {items?.map((item, i) => {
          const isOpen = openItem[i] ?? false
          const SubItem = item.subItem
          const isInstallments = item.label === 'Parcelas'

          return (
            <>
              <hr className={cn("w-full h-[2px] bg-gray-400", i == 0 && 'h-[3px] bg-primary')} />
              <div key={item.label} className={cn("flex flex-col  w-full py-1", isInstallments && 'py-0')}>
                <div className="flex items-center w-full">
                  <span
                    onClick={() => item.subItem && setOpenItem((prev) => ({ ...prev, [i]: !isOpen }))}
                    className={cn("text-primary text-xs mr-1", item.subItem && 'cursor-pointer')}>
                    {isOpen ? '▼' : '▶'}
                  </span>
                  <span className="leading-none text-sm md:text-base">{item.label}:</span>
                  <span
                    className="flex-1 mx-1 h-[1px]"
                    style={{
                      backgroundImage:
                        " repeating-linear-gradient(to right, currentColor 0, currentColor 3px, transparent 3px, transparent 6px)",
                    }}
                  ></span>
                  {isInstallments ? (
                    <div>
                      <Select
                        options={[...options, { label: 'Máximo', value: 'maximo' }]}
                        onChange={(value) => handleSelectInstallment(value as OptionType)}
                        value={selectedOption}
                        styles={customStyles}
                        isDisabled={isReloading}
                      /*   isLoading={isFetchingCustomer || isLoadingCustomer}
                      onMenuScrollToBottom={handleLoadMoreCustomer} */
                      />
                    </div>
                  ) : isReloading ? (
                    <Skeleton className="h-4 w-24" />
                  ) : (
                    <span className="leading-none font-semibold">
                      {item.value}
                    </span>

                  )}
                </div>

                {SubItem && isOpen && (
                  <SubItem data={table.installments ?? []} tc={table.tc} iof={table.iof} seguro={table.seguro} isReloading={isReloading} />
                )}
              </div>
            </>
          )
        })}

        <Button
          disabled={isReloading}
          isLoading={isReloading}
          onClick={handleNext}
          className="text-white mt-2 rounded-xl font-semibold w-full">
          Contratar
        </Button>
      </CreditCard>
    </>
  )
}

const SubValues = ({ tc, iof, seguro, isReloading }: { tc: number, iof: number, seguro: number, isReloading?: boolean }) => {

  const items = [
    {
      label: 'Taxa de Cadastro',
      value: addMoneySimbols(tc),
    },
    {
      label: 'IOF',
      value: addMoneySimbols(iof),
    },
    {
      label: 'Seguro',
      value: addMoneySimbols(seguro),
    },
  ]
  return (
    <div className="p-2 mt-6 rounded-xl border border-gray-300 ">
      <table className="w-full border-collapse text-sm">
        <tbody>
          {items.map((item) => (
            <tr key={item.label}>
              <td className="px-3 py-[2px]	font-medium text-center text-sm">{item.label}</td>
              <td className="px-3 py-[2px]	border-l text-sm border-gray-300 text-center font-medium">
                {isReloading ? <Skeleton className="h-4 w-16 mx-auto" /> : `-${item.value}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const InstallmentItens = ({ data, isReloading }: { data: { number: number, value: number, dueDate: string }[], isReloading?: boolean }) => {
  const styleValues = 'px-4 py-[2px] text-sm font-medium text-left'

  const safeFormatDate = (dueDate: string) => {
    const parsed = parseISO(dueDate)
    if (isValid(parsed)) return format(parsed, 'dd/MM/yyyy')

    const fallback = new Date(dueDate)
    if (isValid(fallback)) return format(fallback, 'dd/MM/yyyy')

    return dueDate
  }

  const rows = (isReloading && (!data || data.length === 0))
    ? Array.from({ length: 6 }, (_, i) => ({ number: i + 1, value: 0, dueDate: '' }))
    : data

  return (
    <div className="p-2 mt-6 rounded-xl border border-gray-300 ">

      <table className="w-full border-collapse text-sm">
        <tbody >
          {rows?.map((item: any) => (
            <tr key={item.number} >
              <td className={styleValues}>
                {item.number}ª Parcela
              </td>

              <td className={cn(styleValues, 'border-l border-gray-300 text-center')}>
                {isReloading ? <Skeleton className="h-4 w-20 mx-auto" /> : addMoneySimbols(item.value)}
              </td>

              <td className={cn(styleValues, 'border-l border-gray-300 text-right')}>
                {isReloading ? <Skeleton className="h-4 w-20 ml-auto" /> : safeFormatDate(item.dueDate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

